/**
 * Single place where every backend call goes out.
 *
 * Contract is inherited from cwc-backend-v1, not invented here — see
 * docs/internal-ops-console-phase1-spec.md §4 and the backend's
 * docs/auth/api-contract.md (the SoT for endpoint shapes).
 *
 *   Base path : /api/v1, admin group /api/v1/admin/*
 *   Envelope  : { code, message, result } — code 0 is success
 *   Session   : opaque token in the httpOnly `cwc_session` cookie
 *
 * Branch on `code`, NOT only on HTTP status: most business failures come back
 * 200-shaped. The genuine transport statuses are 401 (no/expired session) and
 * 403 (role not permitted).
 */

import { IS_MOCK } from '@/mocks/config'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/**
 * /auth/* (OAuth login, logout) is served by cwc-backend-v1, a separate
 * deployment on its own domain — NOT this console's own admin backend, which
 * only ever mounts /api/v1/admin/*. Pointing an /auth/* call at BASE_URL 404s
 * in every real environment; only local dev's single-proxy setup ever made
 * that look like it worked.
 */
export const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL ?? BASE_URL

/** Business codes from the backend's pkg/response. */
export const CODE = {
  SUCCESS: 0,
  INVALID_PARAMS: 1001,
  UNAUTHENTICATED: 1401,
  FORBIDDEN: 1403,
  NOT_FOUND: 1404,
  INVALID_STATE: 2001,
  CONFLICT: 2002,
  /**
   * Server-side failure. Distinct from INVALID_STATE, which means the row moved
   * under you — ConfirmAction shows "Already actioned" for that, which reads as
   * a reassurance and must never appear for a 500. The message is safe to show
   * but deliberately carries no detail; the detail is in the server log.
   *
   * Returned console-wide as of 2026-08-19. Every handler used to put internal
   * failures on 2001 with the raw driver string attached, so ConfirmAction and
   * rewards-page could both show "already actioned" for a server crash.
   */
  INTERNAL: 5000,
} as const

export interface Envelope<T> {
  code: number
  message: string
  result: T
}

/** Backend pagination shape (dtos.PageResult). */
export interface PageResult<T> {
  list: T[]
  total: number
  page: number
  page_size: number
}

export class ApiError extends Error {
  constructor(
    /** HTTP status. 200 for business-code failures. */
    readonly status: number,
    /** Envelope code, or -1 when the failure was purely transport-level. */
    readonly code: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorized() {
    return this.status === 401 || this.code === CODE.UNAUTHENTICATED
  }

  /** Logged in, but not allowed — an RBAC denial. */
  get isForbidden() {
    return this.status === 403 || this.code === CODE.FORBIDDEN
  }

  /**
   * The row was not in the state the action required — almost always because
   * another operator got there first. Retrying the same call cannot help; the
   * list has to be reloaded and the decision reconsidered.
   */
  get isInvalidState() {
    return this.code === CODE.INVALID_STATE
  }
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1]
}

/**
 * OPEN (spec §3.4 / §9 Q2): the backend has no CSRF precedent yet. Below is the
 * double-submit pattern its audit doc proposes. If the admin service ends up
 * owning staff identity, the console and its API are same-origin and
 * SameSite=Lax is enough — delete this then. If it picks a different header,
 * change it here and nowhere else.
 */
function csrfHeaders(method: string): Record<string, string> {
  if (method === 'GET' || method === 'HEAD') return {}
  const token = readCookie('csrf_token')
  return token ? { 'X-CSRF-Token': decodeURIComponent(token) } : {}
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown }

export async function request<T>(
  path: string,
  options: RequestOptions = {},
  baseUrl: string = BASE_URL,
): Promise<T> {
  const { body, headers, ...rest } = options
  const method = (rest.method ?? 'GET').toUpperCase()

  let status: number
  let payload: unknown

  // Dev-only short-circuit. Dynamic import keeps fixtures out of the prod
  // bundle entirely (see src/mocks/config.ts). Mocks return the same envelope
  // the backend does, so they exercise the unwrapping below rather than
  // bypassing it.
  const mocked = IS_MOCK
    ? await (await import('@/mocks/handlers')).resolveMock(method, path, body)
    : null

  if (mocked) {
    status = mocked.status
    payload = mocked.body
  } else {
    // FormData must go out untouched: the browser has to set its own
    // multipart Content-Type with the boundary, and JSON.stringify would
    // reduce the whole thing to "{}".
    const isMultipart = body instanceof FormData

    const response = await fetch(`${baseUrl}${path}`, {
      ...rest,
      method,
      // The whole auth model depends on this line.
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && !isMultipart ? { 'Content-Type': 'application/json' } : {}),
        ...csrfHeaders(method),
        ...headers,
      },
      body: isMultipart ? body : body !== undefined ? JSON.stringify(body) : undefined,
    })

    status = response.status
    if (status === 204) return undefined as T
    payload = await response.json().catch(() => undefined)
  }

  const envelope = payload as Envelope<T> | undefined

  // Transport-level failure — there may be no usable envelope at all.
  if (status >= 400) {
    throw new ApiError(status, envelope?.code ?? -1, envelope?.message || `HTTP ${status}`, payload)
  }

  // 200-shaped business failure.
  if (envelope && envelope.code !== CODE.SUCCESS) {
    throw new ApiError(status, envelope.code, envelope.message || `Error ${envelope.code}`, payload)
  }

  return envelope?.result as T
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/** Only for cwc-backend-v1's /auth/* routes — see AUTH_BASE_URL above. */
export const authApi = {
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }, AUTH_BASE_URL),
}
